import React from 'react';
import {
  CheckCircle2,
  CircleDashed,
  TriangleAlert,
} from 'lucide-react';

import {
  WORKFORCE_PROFILE_CAVEATS,
  buildWorkforceProfilePresentation,
  formatPublishedEmployment,
  formatPublishedHourlyWage,
  formatPublishedPercent,
  workforceObservationProvenance,
} from '../../../lib/findings/workforceProfilePresentation';
import type { EconomicProfileSnapshot } from '../../../types/economicContribution';
import { StatusPill, type StatusPillVariant } from '../../ui/status-pill';

export interface WorkforceProfileSummaryProps {
  profile: EconomicProfileSnapshot;
}

const STATUS_STYLE: Record<
  ReturnType<typeof buildWorkforceProfilePresentation>['status'],
  { variant: StatusPillVariant; icon: typeof CheckCircle2 }
> = {
  available: { variant: 'success', icon: CheckCircle2 },
  partial: { variant: 'warn', icon: TriangleAlert },
  unavailable: { variant: 'neutral', icon: CircleDashed },
};

function formatGeneratedAt(value: string): string {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp)
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(timestamp))
    : value;
}

export const WorkforceProfileSummary: React.FC<WorkforceProfileSummaryProps> = ({ profile }) => {
  const presentation = buildWorkforceProfilePresentation(profile);
  const statusStyle = STATUS_STYLE[presentation.status];
  const hasProviderIssue = presentation.workforceProviderIssueCount > 0;
  const hasMissingMetric = presentation.missingWorkforceMetricCount > 0;

  return (
    <section
      className="mt-5 min-w-0 border-y border-border-subtle py-4"
      aria-labelledby="workforce-profile-heading"
      data-testid="workforce-profile-summary"
      data-availability={presentation.status}
    >
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 max-w-[64ch]">
          <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.13em] text-primary">
            Public workforce profile
          </p>
          <h4
            id="workforce-profile-heading"
            className="mt-1 font-sans text-lg font-semibold tracking-[-0.01em] text-text-primary"
          >
            Local workforce snapshot
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-text-secondary">
            Published employment and wage context for roles relevant to data-center operations.
          </p>
        </div>
        <StatusPill variant={statusStyle.variant} icon={statusStyle.icon}>
          {presentation.statusLabel}
        </StatusPill>
      </header>

      <div className="mt-4 border-y border-border-subtle py-3" aria-labelledby="labor-market-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h5 id="labor-market-heading" className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-text-primary">
            County labor market
          </h5>
          <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted">
            {presentation.countyLabel}
          </p>
        </div>
        <dl className="mt-3 grid min-w-0 grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="workforce-labor-market">
          <div className="min-w-0">
            <dt className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-text-muted">Labor force</dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-text-primary">
              {presentation.laborForce
                ? formatPublishedEmployment(presentation.laborForce.value)
                : <span className="text-sm font-normal text-text-muted">Not published</span>}
            </dd>
            {presentation.laborForce ? (
              <p className="mt-1 break-words font-mono text-[9px] leading-relaxed text-text-muted">
                {workforceObservationProvenance(presentation.laborForce)}
              </p>
            ) : null}
          </div>
          <div className="min-w-0">
            <dt className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-text-muted">Unemployment rate</dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-text-primary">
              {presentation.unemploymentRate
                ? formatPublishedPercent(presentation.unemploymentRate.value)
                : <span className="text-sm font-normal text-text-muted">Not published</span>}
            </dd>
            {presentation.unemploymentRate ? (
              <p className="mt-1 break-words font-mono text-[9px] leading-relaxed text-text-muted">
                {workforceObservationProvenance(presentation.unemploymentRate)}
              </p>
            ) : null}
          </div>
        </dl>
      </div>

      <div className="mt-4" aria-labelledby="occupation-facts-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h5 id="occupation-facts-heading" className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-text-primary">
            Relevant occupations
          </h5>
          <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted">
            Employment · Median hourly wage
          </p>
        </div>
        <div className="mt-2 divide-y divide-border-subtle border-y border-border-subtle" data-testid="workforce-occupation-groups">
          {presentation.roleGroups.map((group) => (
            <section key={group.id} className="min-w-0 py-3" aria-labelledby={`workforce-group-${group.id}`}>
              <div className="grid min-w-0 grid-cols-1 gap-2 md:grid-cols-[minmax(10rem,0.72fr)_minmax(0,1.6fr)] md:gap-6">
                <div className="min-w-0">
                  <h6 id={`workforce-group-${group.id}`} className="text-sm font-semibold text-text-primary">{group.label}</h6>
                  <p className="mt-0.5 text-xs tabular-nums text-text-secondary">
                    {group.publishedEmploymentTotal != null
                      ? `${formatPublishedEmployment(group.publishedEmploymentTotal)} employed across ${group.occupations.length} ${group.occupations.length === 1 ? 'occupation' : 'occupations'}`
                      : group.occupations.some((occupation) => occupation.employment != null)
                        ? 'Partial employment coverage; no combined total shown'
                        : 'Employment not published'}
                  </p>
                  <p className="mt-1 break-words font-mono text-[9px] leading-relaxed text-text-muted">{group.provenance}</p>
                </div>
                <dl className="min-w-0 divide-y divide-border-subtle/70">
                  {group.occupations.map((occupation) => (
                    <div
                      key={occupation.occupationCode}
                      className="grid min-w-0 grid-cols-1 gap-1 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-4"
                    >
                      <dt className="min-w-0 text-xs font-medium leading-snug text-text-primary">
                        {occupation.occupationLabel}
                      </dt>
                      <dd className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs tabular-nums text-text-secondary sm:justify-end sm:text-right">
                        <span>
                          {occupation.employment
                            ? `${formatPublishedEmployment(occupation.employment.value)} employed`
                            : 'Employment not published'}
                        </span>
                        <span aria-hidden className="text-border-strong">·</span>
                        <span>
                          {occupation.medianHourlyWage
                            ? `${formatPublishedHourlyWage(occupation.medianHourlyWage.value)} median`
                            : 'Wage not published'}
                        </span>
                        {group.provenance.startsWith('Mixed') && occupation.provenance.length > 0 ? (
                          <span className="basis-full block w-full break-words font-mono text-[9px] leading-relaxed text-text-muted">
                            {occupation.provenance.join(' · ')}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </section>
          ))}
        </div>
      </div>

      {presentation.status !== 'available' || hasProviderIssue || hasMissingMetric ? (
        <div className="mt-4 border-l-2 border-warning bg-warning/5 px-3 py-2" role="status" data-testid="workforce-profile-availability-note">
          <p className="text-xs leading-relaxed text-text-secondary">
            <span className="font-semibold text-text-primary">Coverage note:</span>{' '}
            {presentation.status === 'unavailable'
              ? 'No published workforce observations are available in this snapshot.'
              : `${presentation.coveredFactCount} of ${presentation.expectedFactCount} requested workforce facts are published.`}
            {hasMissingMetric ? ` ${presentation.missingWorkforceMetricCount} requested ${presentation.missingWorkforceMetricCount === 1 ? 'field remains' : 'fields remain'} unavailable or suppressed; no substitute value is shown.` : ''}
            {hasProviderIssue ? ' One or more workforce sources could not be refreshed.' : ''}
          </p>
        </div>
      ) : null}

      <details className="mt-4 border-y border-border-subtle py-3">
        <summary id="workforce-caveats-heading" className="cursor-pointer list-none font-mono text-[9px] font-semibold uppercase tracking-[0.11em] text-text-muted">
          Sources and limitations
        </summary>
        <p className="mt-2 text-xs leading-relaxed text-text-secondary">
          Occupation facts use the OEWS labor-market area shown; labor-force indicators use the LAUS geography shown.
        </p>
        <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-text-secondary">
          {WORKFORCE_PROFILE_CAVEATS.map((caveat) => <li key={caveat}>{caveat}</li>)}
        </ul>
      </details>

      <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted">
        Profile assembled {formatGeneratedAt(presentation.generatedAt)} · County FIPS {profile.countyFips}
      </p>
    </section>
  );
};
